import { type Olympus } from '@owner/olympus-client'
import { useQuery } from '@tanstack/vue-query'
import {
  computed,
  ref,
  unref,
  watch,
  type ComputedRef,
  type MaybeRef,
} from 'vue'

import { OlympusClient } from '@/OlympusClient'

const QUERY_KEY_PREFIX = ['capabilities', 'brand'] as const

type BrandCapabilities = NonNullable<Olympus.Capabilities.BrandCapabilities>
type BrandNamespace = keyof BrandCapabilities
type CapabilityName<N extends BrandNamespace> = keyof NonNullable<
  BrandCapabilities[N]
>
type CapabilityStatus = 'active' | 'disabled'

export const onboardingEligibleCapabilityActive = ref(false)
export const websiteBuilderCapabilityActive = ref(false)

/**
 * The latest brand-capabilities payload, mirrored into a module ref so non-component
 * code (e.g. the static nav map's `show`/`gated` resolvers) can read capability
 * status reactively via {@link getBrandCapabilityStatus}.
 */
const brandCapabilities = ref<BrandCapabilities>()

/**
 *
 * @param brandId
 */
export function useBrandCapabilitiesQuery(
  brandId: MaybeRef<string | undefined>
) {
  const query = useQuery({
    enabled: computed(() => !!unref(brandId)),
    queryFn: async ({ queryKey }) => {
      const [, , qBrandId] = queryKey

      if (!qBrandId) {
        throw new Error('Brand ID is required')
      }

      return await OlympusClient.capabilities.v1.brands.brand.getBrandCapabilities(
        {
          params: { brandId: qBrandId },
          query: { namespaces: ['marketing', 'onboarding', 'website-builder'] },
        }
      )
    },
    queryKey: [...QUERY_KEY_PREFIX, brandId] as const,
    staleTime: 5 * 60 * 1000,
  })

  watch(
    query.data,
    (data) => {
      brandCapabilities.value = data
    },
    { immediate: true }
  )

  return query
}

/**
 * Reads a brand capability's status from the shared capabilities payload. Returns
 * `undefined` until the payload has loaded — callers must distinguish "not loaded"
 * from "disabled". Safe to call from module scope (the nav map) since it reads a
 * module ref rather than component-injected state.
 *
 * @param namespace
 * @param name
 */
export function getBrandCapabilityStatus<N extends BrandNamespace>(
  namespace: N,
  name: CapabilityName<N>
): CapabilityStatus | undefined {
  const namespaceGroup = brandCapabilities.value?.[namespace] as
    | Record<string, { status?: CapabilityStatus }>
    | undefined
  return namespaceGroup?.[name as string]?.status
}

/**
 * Reactive "is this brand capability active?" check, modelled on Hestia's
 * `useCapability`. Use this in components instead of bespoke per-capability hooks.
 *
 * @param namespace
 * @param name
 */
export function useCapability<N extends BrandNamespace>(
  namespace: N,
  name: CapabilityName<N>
): ComputedRef<boolean> {
  return computed(() => getBrandCapabilityStatus(namespace, name) === 'active')
}

/**
 *
 * @param brandId
 */
export function useOnboardingEligibleCapability(
  brandId: MaybeRef<string | undefined>
): {
  isActive: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
} {
  const { data, isLoading } = useBrandCapabilitiesQuery(brandId)

  const isActive = computed(() => {
    const capabilities = data.value as
      | Record<string, Record<string, { status: string }>>
      | undefined
    return (
      capabilities?.onboarding?.['onboarding-eligible']?.status === 'active'
    )
  })

  watch(
    isActive,
    (v) => {
      onboardingEligibleCapabilityActive.value = v
    },
    { immediate: true }
  )

  return {
    isActive,
    isLoading: computed(() => isLoading.value),
  }
}

/**
 *
 * @param brandId
 */
export function useWebsiteBuilderCapability(
  brandId: MaybeRef<string | undefined>
): {
  isActive: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
} {
  const { data, isLoading } = useBrandCapabilitiesQuery(brandId)

  const isActive = computed(
    () =>
      data.value?.['website-builder']?.['website-builder']?.status === 'active'
  )

  watch(
    isActive,
    (v) => {
      websiteBuilderCapabilityActive.value = v
    },
    { immediate: true }
  )

  return {
    isActive,
    isLoading: computed(() => isLoading.value),
  }
}
